import { COLOR, HEX_OPACITY } from '@app/cnst/color.cnst'
import { ICON } from '@app/cnst/icons.cnst'
import { _numberEnumValues, StringMap } from '@naturalcycles/js-lib'
import {
  AppTracker,
  CervicalMucusConsistency,
  DataFlag,
  DataQuantity,
  HadSex,
  Libido,
  OvulationStatus,
  Sleep,
  TestResult,
} from '@naturalcycles/shared'
import { GradientDrawing, GraphIcon, HolisticGraphEntry, MensStatus } from './graph.model.js'

export enum GraphSource {
  HOME_HEADER_BUTTON = 'HomeHeaderButton',
  MY_CYCLE = 'MyCycle',
  MY_TEMPERATURES = 'MyTemperatures',
  DEEPLINK = 'Deeplink',
  OVULATION_ACHIEVEMENT = 'OvulationAchievement',
  ADD_DATA_CLOSE = 'AddDataClose',
  ALGO_BOX = 'AlgoBox',
  ALGO_BOX_BUTTON = 'AlgoBoxButton',
  TRACKERS_TODAY = 'TrackersToday',
  TRACKERS_CARD_TODAY_AB_TEST = 'TrackersCardTodayABTest',
}

export const GRAPH_OVULATION_TEXT: Record<OvulationStatus, string> = {
  [OvulationStatus.OVU_CONFIRMED]: 'txt-ovulation',
  [OvulationStatus.OVU_DAY]: 'txt-ovulation-predicted',
  [OvulationStatus.OVU_PREDICTION]: 'txt-ovulation-predicted',
  [OvulationStatus.OVU_WAITING]: 'txt-ovulation-waiting',
  [OvulationStatus.OVU_NOT_CONFIRMED]: 'txt-ovulation-not-confirmed',
  [OvulationStatus.ANOVULATORY]: 'txt-ovulation-no',
}

// Icons
// Remember to add all these maps to `GraphService.loadImages()`

export const GRAPH_ICON: StringMap<GraphIcon> = {
  PLUS: { icon: ICON.ADD, color: COLOR.PURPLE },
  PMS_PREDICTION: { icon: ICON.MOOD_PMS, color: COLOR.PREDICTION },
  MORE: { icon: ICON.MORE_HORIZONTAL, color: COLOR.WHITE },
  NOTES: { icon: ICON.NOTES },
  THERMOMETER: { icon: ICON.THERMOMETER, color: COLOR.WHITE },
  OURA: { icon: ICON.OURA, color: COLOR.WHITE },
  APPLE_WATCH: { icon: ICON.APPLE_WATCH, color: COLOR.WHITE, colorDark: COLOR.BLACK },
}

const COLOR_BY_TRACKER: Partial<Record<AppTracker | string, string>> = {
  [AppTracker.MOOD]: '#72035D',
  [AppTracker.PAIN]: '#AC2E00',
  [AppTracker.SKIN]: '#B12A25',
  [AppTracker.SEX]: '#A93059',
  [AppTracker.LIBIDO]: '#A93059',
  [AppTracker.CM_CONSISTENCY]: '#8F1F83',
  [AppTracker.CM_QUANTITY]: '#8F1F83',
  [AppTracker.LH_TEST]: '#16427B',
  [AppTracker.SLEEP]: '#4D5478',
  [AppTracker.MENS_QUANTITY]: '#72035D',
  [AppTracker.MORE_SEX]: '#A93059',
  [AppTracker.PREG_TEST]: '#16427B',
  [AppTracker.LIFESTYLE]: '#1a5c41',
  [AppTracker.EXTERNAL_FACTOR]: '#915f04',
  EMERGENCY: '#6C1612',
  DEVIATION: '#6C1612',
}

const GRAPH_ICON_COLORS_BY_DATAFLAG: Partial<Record<DataFlag, string>> = {
  [DataFlag.DEVIATION_REASON_ALCOHOL]: '#6C1612',
  [DataFlag.DEVIATION_REASON_ALGO]: '#6C1612',
  [DataFlag.DEVIATION_REASON_OTHER]: '#6C1612',
  [DataFlag.DEVIATION_REASON_SICK]: '#6C1612',
  [DataFlag.DEVIATION_REASON_SLEEP]: '#6C1612',
  [DataFlag.OURA_DEVIATION_ALGO]: '#6C1612',
  [DataFlag.OURA_SICK]: '#6C1612',
  [DataFlag.OURA_ALCOHOL]: '#6C1612',
  [DataFlag.OURA_IRREGULAR_SLEEP]: '#ffffff',
  [DataFlag.OURA_ADJUSTED_TEMPERATURE]: '#ffffff',
  [DataFlag.MORE_EMERGENCY_IUD]: '#6C1612',
  [DataFlag.MORE_EMERGENCY_PILL]: '#6C1612',
}

export const GRAPH_ICON_BY_MENS: Record<MensStatus, GraphIcon> = {
  [MensStatus.CONFIRMED]: { icon: ICON.MENSTRUATION, color: COLOR.BLACK },
  [MensStatus.PREDICTION]: { icon: ICON.MENSTRUATION, color: COLOR.PREDICTION },
  [MensStatus.SPOTTING]: {
    icon: ICON.SPOTTING,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [MensStatus.WITHDRAWAL]: { icon: ICON.WITHDRAWAL_MEDIUM, color: COLOR.BLACK },
  [MensStatus.MISCARRIAGE]: { icon: ICON.MISCARRIAGE_MEDIUM, color: COLOR.BLACK },
  [MensStatus.POSTPARTUM]: { icon: ICON.POSTPARTUM_MEDIUM, color: COLOR.BLACK },
}

export const GRAPH_ICON_BY_MENSFLOW: Record<DataQuantity, GraphIcon | undefined> = {
  [DataQuantity.NONE]: undefined,
  [DataQuantity.LIGHT]: {
    icon: ICON.MENSTRUATION_LIGHT,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.MEDIUM]: {
    icon: ICON.MENSTRUATION_MEDIUM,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.HEAVY]: {
    icon: ICON.MENSTRUATION_HEAVY,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
}

export const GRAPH_ICON_BY_MISCARRIAGEFLOW: Record<DataQuantity, GraphIcon | undefined> = {
  [DataQuantity.NONE]: undefined,
  [DataQuantity.LIGHT]: {
    icon: ICON.MISCARRIAGE_LIGHT,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.MEDIUM]: {
    icon: ICON.MISCARRIAGE_MEDIUM,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.HEAVY]: {
    icon: ICON.MISCARRIAGE_HEAVY,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
}

export const GRAPH_ICON_BY_WITHDRAWALFLOW: Record<DataQuantity, GraphIcon | undefined> = {
  [DataQuantity.NONE]: undefined,
  [DataQuantity.LIGHT]: {
    icon: ICON.WITHDRAWAL_LIGHT,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.MEDIUM]: {
    icon: ICON.WITHDRAWAL_MEDIUM,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.HEAVY]: {
    icon: ICON.WITHDRAWAL_HEAVY,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
}

export const GRAPH_ICON_BY_POSTPARTUMFLOW: Record<DataQuantity, GraphIcon | undefined> = {
  [DataQuantity.NONE]: undefined,
  [DataQuantity.LIGHT]: {
    icon: ICON.POSTPARTUM_LIGHT,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.MEDIUM]: {
    icon: ICON.POSTPARTUM_MEDIUM,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
  [DataQuantity.HEAVY]: {
    icon: ICON.POSTPARTUM_HEAVY,
    color: COLOR_BY_TRACKER[AppTracker.MENS_QUANTITY],
  },
}

export const GRAPH_ICON_BY_MENSFLOW_PREDICTION: Record<DataQuantity, GraphIcon | undefined> = {
  [DataQuantity.NONE]: undefined,
  [DataQuantity.LIGHT]: { icon: ICON.MENSTRUATION_LIGHT, color: COLOR.PREDICTION },
  [DataQuantity.MEDIUM]: { icon: ICON.MENSTRUATION_MEDIUM, color: COLOR.PREDICTION },
  [DataQuantity.HEAVY]: { icon: ICON.MENSTRUATION_HEAVY, color: COLOR.PREDICTION },
}

export const GRAPH_ICON_BY_PREGTEST: Record<TestResult, GraphIcon> = {
  [TestResult.YES]: {
    icon: ICON.PREG_TEST_POS,
    color: COLOR_BY_TRACKER[AppTracker.PREG_TEST],
  },
  [TestResult.NO]: {
    icon: ICON.PREG_TEST_NEG,
    color: COLOR_BY_TRACKER[AppTracker.PREG_TEST],
  },
}

export const GRAPH_ICON_BY_PREGTEST_PREVENT: Record<TestResult, GraphIcon> = {
  [TestResult.YES]: {
    icon: ICON.PREG_TEST_POS,
    color: COLOR_BY_TRACKER[AppTracker.PREG_TEST],
  },
  [TestResult.NO]: {
    icon: ICON.PREG_TEST_NEG,
    color: COLOR_BY_TRACKER[AppTracker.PREG_TEST],
  },
}

export const GRAPH_ICON_BY_LH: Record<TestResult, GraphIcon> = {
  [TestResult.YES]: { icon: ICON.LH_POS, color: COLOR_BY_TRACKER[AppTracker.LH_TEST] },
  [TestResult.NO]: { icon: ICON.LH_NEG, color: COLOR_BY_TRACKER[AppTracker.LH_TEST] },
}

export const GRAPH_ICON_BY_SEX: Record<HadSex, GraphIcon | undefined> = {
  [HadSex.YES]: { icon: ICON.SEX, color: COLOR_BY_TRACKER[AppTracker.SEX] },
  [HadSex.YES_PROTECTED]: { icon: ICON.SEX_SAFE, color: COLOR_BY_TRACKER[AppTracker.SEX] },
  [HadSex.NO]: undefined,
}

export const GRAPH_ICON_BY_LIBIDO: Record<Libido, GraphIcon> = {
  [Libido.LOW]: { icon: ICON.LIBIDO_LOW, color: COLOR_BY_TRACKER[AppTracker.LIBIDO] },
  [Libido.MEDIUM]: { icon: ICON.LIBIDO_MEDIUM, color: COLOR_BY_TRACKER[AppTracker.LIBIDO] },
  [Libido.HIGH]: { icon: ICON.LIBIDO_HIGH, color: COLOR_BY_TRACKER[AppTracker.LIBIDO] },
}

export const GRAPH_ICON_BY_SLEEP: Record<Sleep, GraphIcon> = {
  [Sleep.POOR]: { icon: ICON.SLEEP_POOR, color: COLOR_BY_TRACKER[AppTracker.SLEEP] },
  [Sleep.OKAY]: { icon: ICON.SLEEP_OKAY, color: COLOR_BY_TRACKER[AppTracker.SLEEP] },
  [Sleep.RESTFUL]: { icon: ICON.SLEEP_RESTFUL, color: COLOR_BY_TRACKER[AppTracker.SLEEP] },
}

export const GRAPH_ICON_BY_CM_QUANTITY: Record<DataQuantity, GraphIcon> = {
  [DataQuantity.NONE]: {
    icon: ICON.CM_QUANTITY_NONE,
    color: COLOR_BY_TRACKER[AppTracker.CM_QUANTITY],
  },
  [DataQuantity.LIGHT]: {
    icon: ICON.CM_QUANTITY_LIGHT,
    color: COLOR_BY_TRACKER[AppTracker.CM_QUANTITY],
  },
  [DataQuantity.MEDIUM]: {
    icon: ICON.CM_QUANTITY_MEDIUM,
    color: COLOR_BY_TRACKER[AppTracker.CM_QUANTITY],
  },
  [DataQuantity.HEAVY]: {
    icon: ICON.CM_QUANTITY_HEAVY,
    color: COLOR_BY_TRACKER[AppTracker.CM_QUANTITY],
  },
}

export const GRAPH_ICON_BY_CM_CONSISTENCY: Record<CervicalMucusConsistency, GraphIcon> = {
  [CervicalMucusConsistency.CREAMY]: {
    icon: ICON.CM_CONSISTENCY_CREAMY,
    color: COLOR_BY_TRACKER[AppTracker.CM_CONSISTENCY],
  },
  [CervicalMucusConsistency.EGGWHITE]: {
    icon: ICON.CM_CONSISTENCY_EGGWHITE,
    color: COLOR_BY_TRACKER[AppTracker.CM_CONSISTENCY],
  },
  [CervicalMucusConsistency.STICKY]: {
    icon: ICON.CM_CONSISTENCY_STICKY,
    color: COLOR_BY_TRACKER[AppTracker.CM_CONSISTENCY],
  },
  [CervicalMucusConsistency.WATERY]: {
    icon: ICON.CM_CONSISTENCY_WATERY,
    color: COLOR_BY_TRACKER[AppTracker.CM_CONSISTENCY],
  },
}

export const GRAPH_ICON_BY_DATAFLAG = Object.fromEntries(
  _numberEnumValues(DataFlag).map(flag => {
    const icon = ICON[DataFlag[flag] as keyof typeof ICON]

    const tracker = _numberEnumValues(AppTracker).find(t => DataFlag[flag].includes(AppTracker[t]))
    const color = GRAPH_ICON_COLORS_BY_DATAFLAG[flag] || COLOR_BY_TRACKER[tracker!]

    if (!icon || !color) return [flag, undefined]

    return [
      flag,
      {
        icon,
        color,
      },
    ]
  }),
)

export const GRAPH_ICON_BY_OVULATION: Record<OvulationStatus, GraphIcon> = {
  [OvulationStatus.OVU_CONFIRMED]: { icon: ICON.OVULATION, color: COLOR.BLACK },
  [OvulationStatus.OVU_DAY]: { icon: ICON.OVULATION, color: COLOR.PREDICTION },
  [OvulationStatus.OVU_PREDICTION]: { icon: ICON.OVULATION, color: COLOR.PREDICTION },
  [OvulationStatus.OVU_WAITING]: { icon: ICON.CLOCK, color: COLOR.PREDICTION },
  [OvulationStatus.OVU_NOT_CONFIRMED]: { icon: ICON.INFO, color: COLOR.BLACK },
  [OvulationStatus.ANOVULATORY]: { icon: ICON.INFO, color: COLOR.BLACK },
}

export const GRAPH_ICON_BY_TRACKER: Record<AppTracker, GraphIcon | undefined> = {
  [AppTracker.MENS_QUANTITY]: {
    icon: ICON.MENSTRUATION,
    color: COLOR.BLACK,
    colorDark: COLOR.MEDIUM,
  },
  [AppTracker.CM_QUANTITY]: {
    icon: ICON.CM_QUANTITY_LIGHT,
    color: COLOR.BLACK,
    colorDark: COLOR.MEDIUM,
  },
  [AppTracker.CM_CONSISTENCY]: undefined, // same as quantity
  [AppTracker.LIBIDO]: { icon: ICON.LIBIDO_LOW, color: COLOR.BLACK, colorDark: COLOR.MEDIUM },
  [AppTracker.SLEEP]: { icon: ICON.SLEEP_OKAY, color: COLOR.BLACK, colorDark: COLOR.MEDIUM }, // todo
  [AppTracker.PAIN]: { icon: ICON.PAIN_OTHER, color: COLOR.BLACK, colorDark: COLOR.MEDIUM },
  [AppTracker.MOOD]: { icon: ICON.MOOD_CALM, color: COLOR.BLACK, colorDark: COLOR.MEDIUM },
  [AppTracker.SKIN]: { icon: ICON.SKIN_OTHER, color: COLOR.BLACK, colorDark: COLOR.MEDIUM },
  [AppTracker.MORE_SEX]: undefined, // not in holistic graph
  [AppTracker.NOTES]: undefined, // only for partner settings
  [AppTracker.SEX]: undefined, // only for partner settings
  [AppTracker.LH_TEST]: undefined, // only for partner settings
  [AppTracker.PREG_TEST]: undefined, // only for partner settings
  [AppTracker.BOTTLE_FEEDING]: undefined,
  [AppTracker.BREASTFEEDING]: undefined,
  [AppTracker.PUMPING]: undefined,
  [AppTracker.LIFESTYLE]: {
    icon: ICON.LIFESTYLE_TRACKER,
    color: COLOR.BLACK,
    colorDark: COLOR.MEDIUM,
  },
  [AppTracker.EXTERNAL_FACTOR]: {
    icon: ICON.EXTERNAL_FACTOR_TRACKER,
    color: COLOR.BLACK,
    colorDark: COLOR.MEDIUM,
  },
}

export const LABEL_BY_MENS: Partial<Record<MensStatus, string>> = {
  [MensStatus.CONFIRMED]: 'txt-period',
  [MensStatus.MISCARRIAGE]: 'txt-menstruation-miscarriage',
  [MensStatus.POSTPARTUM]: 'txt-menstruation-postpartum',
}

export const TRACKER_KEYS: (keyof HolisticGraphEntry)[] = [
  'ovulationStatus',
  'cervicalMucus',
  'libido',
  'pain',
  'skin',
  'sleep',
  'mood',
  'notes',
]

export const PAIN_ORDER = [
  DataFlag.PAIN_CRAMPS,
  DataFlag.PAIN_SORE_BREASTS,
  DataFlag.PAIN_HEADACHE,
  DataFlag.PAIN_BACKACHE,
  DataFlag.PAIN_OVULATION,
  DataFlag.PAIN_OTHER,
  DataFlag.PAIN_PELVIC,
  DataFlag.PAIN_HEARTBURN,
  DataFlag.PAIN_SWELLING,
  DataFlag.PAIN_CONTRACTIONS,
  DataFlag.PAIN_NAUSEOUS,
  DataFlag.PAIN_DIARRHEA,
  DataFlag.PAIN_CONSTIPATION,
  DataFlag.PAIN_BLOATING,
  DataFlag.PAIN_CRAVINGS,
]

export const SKIN_ORDER = [
  DataFlag.SKIN_DRY,
  DataFlag.SKIN_OILY,
  DataFlag.SKIN_PUFFY,
  DataFlag.SKIN_ACNE,
  DataFlag.SKIN_GLOWING,
  DataFlag.SKIN_CHLOASMA,
  DataFlag.SKIN_OTHER,
]

export const LIFESTYLE_ORDER = [
  DataFlag.LIFESTYLE_ALCOHOL,
  DataFlag.LIFESTYLE_CAFFEINE,
  DataFlag.LIFESTYLE_SUGAR,
  DataFlag.LIFESTYLE_HIGH_FAT_FOOD,
  DataFlag.LIFESTYLE_SMOKING,
  DataFlag.LIFESTYLE_STRESS,
  DataFlag.LIFESTYLE_THERAPY,
  DataFlag.LIFESTYLE_JOURNALING,
  DataFlag.LIFESTYLE_KEGEL_EXERCISE,
  DataFlag.LIFESTYLE_BREATHING_EXERCISE,
  DataFlag.LIFESTYLE_SUPPLEMENTS,
  DataFlag.LIFESTYLE_MINDFULNESS,
]

export const EXTERNAL_FACTOR_ORDER = [
  DataFlag.EXTERNAL_FACTOR_WEATHER,
  DataFlag.EXTERNAL_FACTOR_TRAVEL,
  DataFlag.EXTERNAL_FACTOR_FEELING_OFF,
]

export const MOOD_ORDER = [
  DataFlag.MOOD_PMS,
  DataFlag.MOOD_TIRED,
  DataFlag.MOOD_CALM,
  DataFlag.MOOD_HAPPY,
  DataFlag.MOOD_SENSITIVE,
  DataFlag.MOOD_ANXIOUS,
  DataFlag.MOOD_IRRITABLE,
  DataFlag.MOOD_STRESSED,
  DataFlag.MOOD_SAD,
  DataFlag.MOOD_ENERGETIC,
  DataFlag.MOOD_SWINGS,
  DataFlag.MOOD_CONFIDENT,
  DataFlag.MOOD_EXCITED,
  DataFlag.MOOD_NUMB,
  DataFlag.MOOD_ANGRY,
  DataFlag.MOOD_EXHAUSTED,
  DataFlag.MOOD_SENSITIVE_TO_SMELL,
  DataFlag.MOOD_SICK,
  DataFlag.MOOD_FORGETFUL,
  DataFlag.MOOD_UNFOCUSED,
  DataFlag.MOOD_SELF_CRITICAL,
  DataFlag.MOOD_FEELING_GUILTY,
  DataFlag.MOOD_OBSESSIVE_THOUGHTS,
  DataFlag.MOOD_CONFUSED,
]

interface GraphTheme {
  backgroundColor: string
  pageBackgroundColor: string // --ion-background-color
  fertileWindowColor: string
  textColor: string // --ion-text-color
  textColorContrast: string // --ion-text-color-contrast
  lineColor: string
  lineColorOpaque: string
  mensColor: string
  mensPredictionColor: string
  ovulationConfirmedGradient: GradientDrawing
  ovulationConfirmedWindowColor: string
  ovulationPredictionColor: string
  ovulationPredictionWindowColor: string
  shadowColor: string
  logoColor: string // --ion-color-logo
  predictionColor: string
  chipColor: string
  chipTextColor: string
}

export const graphThemeLight: GraphTheme = {
  backgroundColor: COLOR.GHOST + HEX_OPACITY[35],
  pageBackgroundColor: COLOR.WHITE,
  fertileWindowColor: COLOR.PINK + HEX_OPACITY[15],
  textColor: COLOR.BLACK,
  textColorContrast: COLOR.WHITE,
  lineColor: COLOR.MEDIUM + HEX_OPACITY[80],
  lineColorOpaque: COLOR.MEDIUM,
  mensColor: COLOR.PURPLE + HEX_OPACITY[15],
  mensPredictionColor: COLOR.MEDIUM + HEX_OPACITY[40],
  ovulationConfirmedGradient: {
    startColor: COLOR.TEMPERATURE + HEX_OPACITY[15],
    stopColor: '#8A1568' + HEX_OPACITY[30],
  },
  ovulationConfirmedWindowColor: COLOR.PURPLE + HEX_OPACITY[15],
  ovulationPredictionColor: COLOR.MEDIUM + HEX_OPACITY[40],
  ovulationPredictionWindowColor: COLOR.MEDIUM + HEX_OPACITY[40],
  shadowColor: COLOR.BLACK + HEX_OPACITY[10],
  logoColor: COLOR.PURPLE,
  predictionColor: COLOR.MEDIUM,
  chipColor: COLOR.PURPLE,
  chipTextColor: COLOR.WHITE,
}

export const graphThemeDark: GraphTheme = {
  backgroundColor: COLOR.GHOST + HEX_OPACITY[5],
  pageBackgroundColor: '#310629',
  fertileWindowColor: COLOR.PINK + HEX_OPACITY[15],
  textColor: COLOR.MEDIUM,
  textColorContrast: COLOR.DARK_PURPLE,
  lineColor: COLOR.MEDIUM + HEX_OPACITY[45],
  lineColorOpaque: COLOR.MEDIUM,
  mensColor: '#FCBBF0' + HEX_OPACITY[25],
  mensPredictionColor: COLOR.SECONDARY_DARK + HEX_OPACITY[15],
  ovulationConfirmedGradient: {
    startColor: COLOR.TEMPERATURE + HEX_OPACITY[80],
    stopColor: '#8A1568' + HEX_OPACITY[15],
  },
  ovulationConfirmedWindowColor: COLOR.SECONDARY_DARK + HEX_OPACITY[15],
  ovulationPredictionColor: COLOR.MEDIUM + HEX_OPACITY[15],
  ovulationPredictionWindowColor: COLOR.MEDIUM + HEX_OPACITY[20],
  shadowColor: COLOR.BLACK + HEX_OPACITY[10],
  logoColor: COLOR.MEDIUM,
  predictionColor: COLOR.SAPHIRE_PINK,
  chipColor: COLOR.SECONDARY_DARK,
  chipTextColor: COLOR.BLACK,
}

export const ovulationColorKeyByStatus: Record<OvulationStatus, keyof GraphTheme | undefined> = {
  [OvulationStatus.OVU_CONFIRMED]: 'ovulationConfirmedGradient',
  [OvulationStatus.OVU_DAY]: 'ovulationPredictionColor',
  [OvulationStatus.OVU_PREDICTION]: 'ovulationPredictionColor',
  [OvulationStatus.OVU_WAITING]: 'ovulationPredictionColor',
  [OvulationStatus.OVU_NOT_CONFIRMED]: 'ovulationPredictionColor',
  [OvulationStatus.ANOVULATORY]: undefined,
}

export const windowColorKeyByOvulationStatus: Record<
  OvulationStatus,
  keyof GraphTheme | undefined
> = {
  [OvulationStatus.OVU_CONFIRMED]: 'ovulationConfirmedWindowColor',
  [OvulationStatus.OVU_DAY]: 'ovulationPredictionWindowColor',
  [OvulationStatus.OVU_PREDICTION]: 'ovulationPredictionWindowColor',
  [OvulationStatus.OVU_WAITING]: 'ovulationPredictionWindowColor',
  [OvulationStatus.OVU_NOT_CONFIRMED]: 'ovulationPredictionWindowColor',
  [OvulationStatus.ANOVULATORY]: undefined,
}

export const DARK_COLOR_MAP = {
  [COLOR.BLACK]: COLOR.WHITE,
  [COLOR.WHITE]: '#310629',
  [COLOR.PREDICTION]: COLOR.MEDIUM,
  [COLOR.PURPLE]: COLOR.MEDIUM,
}
